import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import MaterialTable from 'material-table';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import moment from 'moment/min/moment-with-locales';
import { FirebaseContext, api } from 'common';
import { onValue } from 'firebase/database';
import CircularLoading from '../components/CircularLoading';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import theme from 'styles/tableStyle';
import { colors } from '../components/Theme/WebTheme';
import { SECONDORY_COLOR } from '../common/sharedFunctions';
import { textOrDash } from '../common/adminDisplay';
import { BRAND_ACCENTS, cuisineLabel, cuisineLookup } from '../common/eatCatalog';
import { formatAdminMoney, getEatRestaurantSettlement, getSettlementStyle } from '../common/adminFinance';

const brandLookup = BRAND_ACCENTS.reduce((lookup, brand) => {
  lookup[brand.color] = brand.label;
  return lookup;
}, {});

const dayConfig = [
  { key: 'sunday', label: 'Dimanche' },
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' }
];

const getRestaurantAddress = (restaurant = {}) => restaurant.address || restaurant.restaurantAddress || restaurant.location?.add || restaurant.location?.address || restaurant.city || restaurant.zoneId || '';

const formatOpeningHours = (restaurant = {}) => {
  const openingHours = restaurant.openingHours || {};
  const groups = [];
  let index = 0;

  const dayLabel = (hours = {}) => {
    if (hours.open === false) return 'Fermé';
    const first = `${hours.start || '11:30'} - ${hours.end || '22:30'}`;
    return hours.split && hours.start2 && hours.end2 ? `${first} / ${hours.start2} - ${hours.end2}` : first;
  };

  while (index < dayConfig.length) {
    const currentDay = dayConfig[index];
    const currentHours = openingHours[currentDay.key] || {};
    const label = dayLabel(currentHours);
    let endIndex = index;
    while (endIndex + 1 < dayConfig.length) {
      const nextDay = dayConfig[endIndex + 1];
      const nextHours = openingHours[nextDay.key] || {};
      const nextLabel = dayLabel(nextHours);
      if (nextLabel !== label) break;
      endIndex += 1;
    }
    groups.push(`${currentDay.label}${endIndex > index ? ` - ${dayConfig[endIndex].label}` : ''}: ${label}`);
    index = endIndex + 1;
  }

  return groups.join(' | ');
};

const imagePreview = (url, label) => url ? (
  <img
    src={url}
    alt={label}
    style={{ width: 42, height: 42, borderRadius: 8, objectFit: 'cover', display: 'block' }}
  />
) : '-';

const documentLink = (url) => url ? <a href={url} target="_blank" rel="noreferrer">Voir</a> : '-';
const getRestaurantKbisUrl = (restaurant = {}) => restaurant.kbisUrl || restaurant.kbis || restaurant.documents?.kbisUrl || restaurant.documents?.kbis || '';
const getRestaurantRibUrl = (restaurant = {}) => restaurant.ribUrl || restaurant.rib || restaurant.documents?.ribUrl || restaurant.documents?.rib || '';
const getRestaurantIban = (restaurant = {}) => String(restaurant.iban || restaurant.IBAN || restaurant.documents?.iban || '').replace(/\s+/g, '').toUpperCase();
const getRestaurantBic = (restaurant = {}) => String(restaurant.bic || restaurant.BIC || restaurant.documents?.bic || '').replace(/\s+/g, '').toUpperCase();
const hasRibInfo = (restaurant = {}) => Boolean(getRestaurantRibUrl(restaurant) || getRestaurantIban(restaurant));
const hasRequiredRestaurantDocs = (restaurant = {}) => Boolean(getRestaurantKbisUrl(restaurant) && hasRibInfo(restaurant));
const getPublicationStatus = (restaurant = {}) => hasRequiredRestaurantDocs(restaurant) ? 'Publié automatiquement' : 'K-bis/RIB requis';

const buildRatingSummaries = (ratingsRoot = {}) => {
  return Object.keys(ratingsRoot || {}).reduce((summaries, id) => {
    const reviews = Object.values(ratingsRoot[id] || {})
      .map(review => ({
        ...review,
        rate: Number(review.rate || review.rating || 0),
        createdAt: Number(review.createdAt || review.ratedAt || 0)
      }))
      .filter(review => review.rate > 0);
    const ratingCount = reviews.length;
    const rating = ratingCount ? reviews.reduce((sum, review) => sum + review.rate, 0) / ratingCount : 0;
    const latestReview = reviews.sort((left, right) => right.createdAt - left.createdAt)[0] || null;
    summaries[id] = {
      rating: rating ? Number(rating.toFixed(1)) : null,
      ratingCount,
      latestFeedback: latestReview && latestReview.feedback ? latestReview.feedback : '',
      latestRatedAt: latestReview ? latestReview.createdAt : null
    };
    return summaries;
  }, {});
};

export default function EatRestaurants() {
  const dispatch = useDispatch();
  const firebase = useContext(FirebaseContext);
  const loaded = useRef(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const restaurants = useSelector(state => state.eatdata.restaurants || []);
  const orders = useSelector(state => state.eatdata.orders || []);
  const loading = useSelector(state => state.eatdata.loading);
  const settings = useSelector(state => state.settingsdata.settings || {});
  const [selectedRow, setSelectedRow] = useState(null);
  const [ratingSummaries, setRatingSummaries] = useState({});

  useEffect(() => {
    dispatch(api.fetchEatRestaurants());
    dispatch(api.fetchEatOrders());
    loaded.current = true;
  }, [dispatch]);

  useEffect(() => {
    if (!firebase || !firebase.eatRatingsRef) return undefined;
    return onValue(firebase.eatRatingsRef, snapshot => {
      const ratingsRoot = snapshot.val() || {};
      setRatingSummaries(buildRatingSummaries(ratingsRoot.restaurants || {}));
    });
  }, [firebase]);

  const sortedData = useMemo(() => {
    return [...restaurants]
      .filter(restaurant => !restaurant.archived && !restaurant.deletedAt)
      .map(restaurant => {
        const liveRating = ratingSummaries[restaurant.id || restaurant.restaurantId] || {};
        return {
          ...restaurant,
          ...(() => {
            const settlement = getEatRestaurantSettlement(orders, restaurant.id || restaurant.restaurantId);
            return {
              settlementBalance: settlement.platformOwes,
              platformOwesAmount: settlement.platformOwes,
              accountingCommissionAmount: settlement.commission,
              accountingOrderCount: settlement.count,
              accountingLastActivity: settlement.lastActivity
            };
          })(),
          rating: liveRating.rating,
          ratingCount: liveRating.ratingCount || 0,
          latestFeedback: liveRating.latestFeedback || '',
          latestRatedAt: liveRating.latestRatedAt || null
        };
      })
      .sort((left, right) => (right.updatedAt || right.createdAt || 0) - (left.updatedAt || left.createdAt || 0));
  }, [orders, restaurants, ratingSummaries]);

  const updateRestaurant = (rowData, patch) => {
    dispatch(api.updateEatRestaurant(rowData.id || rowData.restaurantId, patch));
  };

  const createRestaurant = (rowData) => {
    const restaurantId = textOrDash(rowData.id, '') || `eat-restaurant-${Date.now()}`;
    dispatch(api.updateEatRestaurant(restaurantId, {
      id: restaurantId,
      name: rowData.name || '',
      ownerUid: rowData.ownerUid || '',
      ownerName: rowData.ownerName || '',
      mobile: rowData.mobile || '',
      email: rowData.email || '',
      cuisine: rowData.cuisine || '',
      specialty: rowData.specialty || '',
      logoUrl: rowData.logoUrl || '',
      coverImageUrl: rowData.coverImageUrl || '',
      kbisUrl: rowData.kbisUrl || rowData.kbis || '',
      ribUrl: rowData.ribUrl || rowData.rib || '',
      iban: String(rowData.iban || '').replace(/\s+/g, '').toUpperCase(),
      bic: String(rowData.bic || '').replace(/\s+/g, '').toUpperCase(),
      brandColor: rowData.brandColor || BRAND_ACCENTS[0].color,
      address: rowData.address || '',
      restaurantAddress: rowData.address || '',
      city: rowData.city || '',
      isOpen: Boolean(rowData.isOpen),
      active: hasRequiredRestaurantDocs(rowData),
      createdAt: Date.now()
    }));
  };

  const columns = [
    {
      title: 'Créé le',
      field: 'createdAt',
      editable: 'never',
      render: rowData => rowData.createdAt ? moment(rowData.createdAt).format('lll') : '-'
    },
    { title: 'ID restaurant', field: 'id', render: rowData => textOrDash(rowData.id) },
    { title: 'Restaurant', field: 'name', render: rowData => textOrDash(rowData.name) },
    { title: 'UID restaurateur', field: 'ownerUid', render: rowData => textOrDash(rowData.ownerUid || rowData.ownerId) },
    { title: 'Responsable', field: 'ownerName', render: rowData => textOrDash(rowData.ownerName || rowData.contactName) },
    { title: 'Téléphone', field: 'mobile', render: rowData => textOrDash(rowData.mobile || rowData.phone) },
    { title: 'E-mail', field: 'email', render: rowData => textOrDash(rowData.email) },
    { title: 'Cuisine', field: 'cuisine', lookup: cuisineLookup, render: rowData => cuisineLabel(rowData.cuisine) },
    { title: 'Spécialité', field: 'specialty', render: rowData => textOrDash(rowData.specialty) },
    { title: 'Logo', field: 'logoUrl', render: rowData => imagePreview(rowData.logoUrl, rowData.name || 'Logo') },
    { title: 'Photo vitrine', field: 'coverImageUrl', render: rowData => imagePreview(rowData.coverImageUrl, rowData.name || 'Photo vitrine') },
    { title: 'K-bis obligatoire', field: 'kbisUrl', render: rowData => documentLink(getRestaurantKbisUrl(rowData)) },
    { title: 'RIB photo (optionnel)', field: 'ribUrl', render: rowData => documentLink(getRestaurantRibUrl(rowData)) },
    { title: 'IBAN', field: 'iban', render: rowData => textOrDash(getRestaurantIban(rowData)) },
    { title: 'BIC', field: 'bic', render: rowData => textOrDash(getRestaurantBic(rowData)) },
    { title: 'Dossier', field: 'documentsStatus', editable: 'never', render: rowData => hasRequiredRestaurantDocs(rowData) ? 'Complet' : 'K-bis/RIB requis' },
    { title: 'Adresse réelle', field: 'address', render: rowData => textOrDash(getRestaurantAddress(rowData)) },
    {
      title: 'Branding',
      field: 'brandColor',
      lookup: brandLookup,
      render: rowData => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 18, height: 18, borderRadius: 5, background: rowData.brandColor || BRAND_ACCENTS[0].color, border: '1px solid rgba(0,0,0,0.12)' }} />
          {brandLookup[rowData.brandColor] || textOrDash(rowData.brandColor)}
        </span>
      )
    },
    { title: 'Ville', field: 'city', render: rowData => textOrDash(rowData.city) },
    { title: 'Horaires', field: 'openingHours', editable: 'never', render: rowData => formatOpeningHours(rowData) },
    {
      title: 'Règlement',
      field: 'settlementBalance',
      type: 'numeric',
      defaultSort: 'desc',
      editable: 'never',
      render: rowData => {
        const style = getSettlementStyle(rowData.settlementBalance);
        return <Chip size="small" label={`À payer ${formatAdminMoney(rowData.platformOwesAmount, settings)}`} sx={{ color: style.color, backgroundColor: style.backgroundColor, border: `1px solid ${style.borderColor}`, fontWeight: 900 }} />;
      }
    },
    { title: 'À payer restaurant', field: 'platformOwesAmount', type: 'numeric', editable: 'never', render: rowData => formatAdminMoney(rowData.platformOwesAmount, settings) },
    { title: 'Commission plateforme', field: 'accountingCommissionAmount', type: 'numeric', editable: 'never', render: rowData => formatAdminMoney(rowData.accountingCommissionAmount, settings) },
    { title: 'Commandes compta', field: 'accountingOrderCount', type: 'numeric', editable: 'never' },
    { title: 'Dernière activité compta', field: 'accountingLastActivity', editable: 'never', render: rowData => rowData.accountingLastActivity ? moment(rowData.accountingLastActivity).format('lll') : '-' },
    {
      title: 'Ouvert',
      field: 'isOpen',
      type: 'boolean',
      render: rowData => (
        <Switch
          disabled={!settings.AllowCriticalEditsAdmin}
          checked={Boolean(rowData.isOpen)}
          onChange={() => updateRestaurant(rowData, { isOpen: !rowData.isOpen, active: hasRequiredRestaurantDocs(rowData) })}
        />
      )
    },
    { title: 'Publication', field: 'active', editable: 'never', render: rowData => getPublicationStatus(rowData) },
    { title: 'Note app', field: 'rating', editable: 'never', type: 'numeric', render: rowData => rowData.rating ? `${Number(rowData.rating).toFixed(1)} / 5` : '-' },
    { title: 'Avis app', field: 'ratingCount', editable: 'never', type: 'numeric', render: rowData => rowData.ratingCount ? rowData.ratingCount : '-' },
    { title: 'Dernier avis', field: 'latestFeedback', editable: 'never', render: rowData => textOrDash(rowData.latestFeedback) },
    { title: 'Dernière note', field: 'latestRatedAt', editable: 'never', render: rowData => rowData.latestRatedAt ? moment(rowData.latestRatedAt).format('lll') : '-' },
    { title: 'Mis à jour', field: 'updatedAt', editable: 'never', render: rowData => rowData.updatedAt ? moment(rowData.updatedAt).format('lll') : '-' }
  ];

  if (!loaded.current && loading) {
    return <CircularLoading />;
  }

  return (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title="Restaurateurs Allô Driver Eats"
        columns={columns}
        data={sortedData}
        onRowClick={(event, rowData) => rowData && setSelectedRow(rowData.tableData.id)}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: '8px',
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: '20px'
        }}
        options={{
          exportButton: { csv: true, pdf: false },
          maxColumnSort: 'all_columns',
          rowStyle: rowData => ({
            backgroundColor: selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
          }),
          ...TableStyle()
        }}
        localization={localization(t)}
        editable={{
          onRowAdd: newData => new Promise(resolve => {
            setTimeout(() => {
              resolve();
              createRestaurant(newData);
            }, 400);
          }),
          onRowUpdate: (newData, oldData) => new Promise(resolve => {
            setTimeout(() => {
              resolve();
              updateRestaurant(oldData, {
                name: newData.name || '',
                ownerUid: newData.ownerUid || '',
                ownerName: newData.ownerName || '',
                mobile: newData.mobile || '',
                email: newData.email || '',
                cuisine: newData.cuisine || '',
                specialty: newData.specialty || '',
                logoUrl: newData.logoUrl || '',
                coverImageUrl: newData.coverImageUrl || '',
                kbisUrl: newData.kbisUrl || newData.kbis || '',
                ribUrl: newData.ribUrl || newData.rib || '',
                iban: String(newData.iban || '').replace(/\s+/g, '').toUpperCase(),
                bic: String(newData.bic || '').replace(/\s+/g, '').toUpperCase(),
                brandColor: newData.brandColor || BRAND_ACCENTS[0].color,
                address: newData.address || '',
                restaurantAddress: newData.address || '',
                location: { ...((oldData && oldData.location) || {}), add: newData.address || '', address: newData.address || '' },
                city: newData.city || '',
                isOpen: Boolean(newData.isOpen),
                active: hasRequiredRestaurantDocs(newData)
              });
            }, 400);
          }),
          onRowDelete: oldData => new Promise(resolve => {
            setTimeout(() => {
              resolve();
              updateRestaurant(oldData, { archived: true, deletedAt: Date.now(), active: false, isOpen: false });
            }, 400);
          })
        }}
      />
    </ThemeProvider>
  );
}
